/**
 * Lobby inbox — what the wider team (anyone with the deck link) said.
 * Live-updating: subscribes to activity events of kind 'lobby.feedback'.
 */

import { useEffect, useState } from 'react';
import { api } from '../api';
import { Modal } from './Modal';
import { IconHeart, IconStar, IconX, IconChat } from './Icon';
import { Spinner } from './Spinner';
import type { BoardDetail } from '../../shared/types';

interface FeedbackRow {
  id: string;
  kind: 'comment' | 'love' | 'curious' | 'pass';
  body: string | null;
  name: string | null;
  email: string | null;
  sessionId: string | null;
  createdAt: number;
}

export function LobbyInbox({
  open,
  onClose,
  board,
}: {
  open: boolean;
  onClose: () => void;
  board: BoardDetail;
}) {
  const [rows, setRows] = useState<FeedbackRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void api
      .lobbyFeedback(board.id)
      .then((r) => {
        if (!cancelled) setRows(r.feedback);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed'));
    return () => {
      cancelled = true;
    };
  }, [open, board.id]);

  return (
    <Modal open={open} onClose={onClose} title="From the lobby" maxWidth={520}>
      {!board.deckShareToken ? (
        <p className="text-sm text-cream-3">
          No public deck link yet — mint one in the Deck phase to start collecting feedback.
        </p>
      ) : !rows ? (
        <div className="flex items-center gap-2 text-xs text-cream-3">
          <Spinner size={12} />
          <span>Loading…</span>
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-cream-3">No-one's left feedback yet.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {rows.slice().reverse().map((r) => (
            <li key={r.id} className="rounded-lg border border-ink-rim/60 bg-ink-3 p-3">
              <header className="mb-1 flex items-center gap-2 text-xs text-cream-3">
                <KindBadge kind={r.kind} />
                <span>{r.name ?? 'Anonymous'}</span>
                <span className="ml-auto">{relative(r.createdAt)}</span>
              </header>
              {r.body && <p className="text-sm">{r.body}</p>}
              {r.sessionId && (
                <p className="mt-1 text-[11px] text-cream-3">
                  On session{' '}
                  {findSessionName(board, r.sessionId) ?? r.sessionId}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
      {error && <p className="mt-2 text-xs text-rust">{error}</p>}
    </Modal>
  );
}

function findSessionName(_b: BoardDetail, _id: string): string | null {
  // We don't have sessions on BoardDetail; this is a name-only convenience.
  // The caller already knows boards.sessions in PlanPhase; here we punt.
  return null;
}

function KindBadge({ kind }: { kind: FeedbackRow['kind'] }) {
  const palette = {
    love: { icon: <IconHeart size={11} />, label: 'Love', color: 'hsl(140 60% 55%)' },
    curious: { icon: <IconStar size={11} />, label: 'Curious', color: 'hsl(42 90% 60%)' },
    pass: { icon: <IconX size={11} />, label: 'Pass', color: 'hsl(0 70% 60%)' },
    comment: { icon: <IconChat size={11} />, label: 'Comment', color: 'var(--color-cream-2)' },
  }[kind];
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wide"
      style={{ color: palette.color, border: `1px solid ${palette.color}` }}
    >
      {palette.icon} {palette.label}
    </span>
  );
}

function relative(unixSec: number): string {
  const d = Math.floor(Date.now() / 1000) - unixSec;
  if (d < 60) return 'just now';
  if (d < 3600) return `${Math.floor(d / 60)}m`;
  if (d < 86400) return `${Math.floor(d / 3600)}h`;
  return `${Math.floor(d / 86400)}d`;
}
